import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { cartService } from '../../../../services/cartService';
import { eventService } from '../../../../services/eventService';
import { productService, ProductVariant } from '../../../../services/productService';

interface UseProductCartOptions {
  agentChatId?: number;
  preloadVariants?: boolean;
}

export function useProductCart(productId: number, options: UseProductCartOptions = {}) {
  const { userInfo } = useAuth();
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showVariants, setShowVariants] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const announce = useCallback((nextMessage: string) => {
    setMessage(nextMessage);
    window.setTimeout(() => setMessage(null), 3000);
  }, []);

  const loadVariants = useCallback(async () => {
    setLoadingVariants(true);
    try {
      const details = await productService.getProductDetails(productId);
      const nextVariants = details?.variants || [];
      setVariants(nextVariants);
      setSelectedVariantId((current) => current || nextVariants[0]?.id || null);
      return nextVariants;
    } catch (error) {
      console.error('Error fetching product variants:', error);
      return [];
    } finally {
      setLoadingVariants(false);
    }
  }, [productId]);

  useEffect(() => {
    setVariants([]);
    setSelectedVariantId(null);
    setShowVariants(false);
    setMessage(null);
    if (options.preloadVariants) loadVariants();
  }, [loadVariants, options.preloadVariants]);

  const addToCart = useCallback(async (variationId?: number | null) => {
    if (!userInfo?.is_authenticated) {
      announce('لطفاً اول وارد حساب کاربریت شو');
      return false;
    }

    const effectiveVariantId = variationId ?? selectedVariantId;
    if (variants.length > 0 && !effectiveVariantId) {
      announce('لطفاً یه گزینه رو انتخاب کن');
      return false;
    }

    setAddingToCart(true);
    setMessage(null);
    try {
      const result = await cartService.addToCart({
        product_id: productId,
        quantity: 1,
        variation_id: effectiveVariantId || undefined,
      });
      if (!result.success) {
        announce(result.message || 'متأسفانه یه مشکلی پیش اومد، به سبد خریدت اضافه نشد.');
        return false;
      }

      announce('✓ به سبد خریدت اضافه شد');
      setShowVariants(false);
      if (options.agentChatId) {
        void eventService.captureProductClick(options.agentChatId, productId, 'addToCart');
      }
      return true;
    } catch (error) {
      announce('متأسفانه یه مشکلی پیش اومد، به سبد خریدت اضافه نشد.');
      return false;
    } finally {
      setAddingToCart(false);
    }
  }, [announce, options.agentChatId, productId, selectedVariantId, userInfo?.is_authenticated, variants.length]);

  const requestAddToCart = useCallback(async () => {
    if (!userInfo?.is_authenticated) {
      announce('لطفاً اول وارد حساب کاربریت شو');
      return;
    }
    if (variants.length > 0) {
      setShowVariants(true);
      return;
    }
    const nextVariants = await loadVariants();
    if (nextVariants.length > 0) {
      setShowVariants(true);
      return;
    }
    await addToCart(null);
  }, [addToCart, announce, loadVariants, userInfo?.is_authenticated, variants.length]);

  const variantOptions = useMemo(() => variants.map((variant) => ({
    variant,
    label: variant.properties.map((property) => `${property.property.title}: ${property.value.title}`).join('، '),
  })), [variants]);

  const selectedPrice = useMemo(
    () => variants.find((variant) => variant.id === selectedVariantId)?.price,
    [selectedVariantId, variants],
  );

  return {
    variants,
    variantOptions,
    selectedVariantId,
    setSelectedVariantId,
    selectedPrice,
    loadingVariants,
    addingToCart,
    showVariants,
    setShowVariants,
    message,
    loadVariants,
    addToCart,
    requestAddToCart,
  };
}
